/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from "react";
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonTextarea,
  IonIcon,
  IonFooter,
} from "@ionic/react";
import { close } from "ionicons/icons";
import "./AddPostModal.css";
import BottomActionsBar from "./BottomActionsBar";
import SectionCards from "./SectionCards";
import { TherapyData } from "../../components/journal/TherapyPreviewCard";
import BiWazeTherapyCard from "../../components/therapy/BiWazeTherapyCard";
import {
  AddPostModalProps,
  SectionType,
  MOODS,
  TemplateCard,
  PostData,
} from "../../types/socialJournal";
import {
  createPost,
  Post,
  updatePost,
  uploadImagesBackendFetch,
} from "../../services/postsService";
import {
  compressImages,
  CompressionPresets,
} from "../../utils/imageCompression";
import Avatar from "../../components/ui/Avatar";
import { getInitials } from "../../utils/helpers";
import { useToaster } from "../../components/ui/toastbar/Toaster";
import Loader from "../../components/helpers/Loader";
import AuthenticatedImage from "../../components/journal/AuthenticatedImage";

interface EnhancedAddPostModalProps extends AddPostModalProps {
  editPost?: Post | null; // Post to edit
  onEditComplete?: () => void; // Callback after edit completes
}

const AddPostModal: React.FC<EnhancedAddPostModalProps> = ({
  isOpen,
  onDidDismiss,
  onPost,
  journalCategories,
  preSelected,
  user,
  setPreSelected,
  editPost,
  onEditComplete,
}) => {
  const [postContent, setPostContent] = useState("");
  const [postPlaceHolder, setPostPlaceHolder] = useState(
    "What's on your mind?"
  );
  const [selectedMood, setSelectedMood] = useState("");
  const [selectedAudience, setSelectedAudience] = useState("Followers");
  const [activeSection, setActiveSection] = useState<SectionType | null>(null);
  const [therapyData, setTherapyData] = useState<TherapyData | null>(null);
  const toaster = useToaster();
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [showAudiencePopover, setShowAudiencePopover] = useState(false);
  const [showMoodSelector, setShowMoodSelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const toastBar = useToaster();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLIonTextareaElement>(null);
  const isEditMode = !!editPost;
  const [inputFieldCharIndex, setInputFieldCharIndex] = useState<number | null>(
    null
  );

  const templateCards: TemplateCard[] = [
    {
      key: "progress",
      title: "Progress",
      description: "What goal are you working on?",
      template:
        "My current goal is to ___. I've completed ___ sessions so far. Next, I want to focus on ___.",
      className: "progress-card",
      inputCharIndex: 22,
    },
    {
      key: "thoughts",
      title: "Thoughts",
      description: "Write what's on your mind today",
      template:
        "Today I'm feeling ___. My therapy has been ___ so far. I want to remember that ___.",
      className: "",
      inputCharIndex: 18,
    },
    {
      key: "inspiration",
      title: "Inspiration",
      description: "What Inspires you?",
      template:
        "This week I managed to ___. I'm proud of ___. A small reminder to myself: ___.",
      className: "",
      inputCharIndex: 23,
    },
  ];

  // Load edit post data when modal opens in edit mode
  useEffect(() => {
    if (isEditMode && editPost) {
      setPostContent(editPost.content || "");
      setSelectedMood(editPost.mood || "");
      setSelectedAudience(
        editPost.visibility === "private" ? "Private" : "Followers"
      );

      // Load existing images
      if (editPost.images && editPost.images.length > 0) {
        setExistingImageUrls(editPost.images);
      }

      // Load therapy data if present
      if (editPost.therapyData) {
        setTherapyData(editPost.therapyData);
      }
    }
  }, [isEditMode, editPost, isOpen]);

  useEffect(() => {
    if (!isEditMode && preSelected) {
      if (preSelected === "text") {
        setActiveSection(null);
        setTherapyData(null);
      } else if (preSelected === "template") {
        setActiveSection("thoughts");
        setTherapyData(null);
      } else if (preSelected === "images") {
        // Add a small delay to ensure the modal is fully rendered and ref is available
        setTimeout(() => {
          if (fileInputRef.current) {
            // fileInputRef.current.setAttribute("capture", "environment");
            fileInputRef.current.click();
          }
        }, 100);
      } else if (preSelected === "feelings") {
        setShowMoodSelector(true);
      }
    }
  }, [preSelected, isEditMode]);

  const selectedMoodObj = MOODS.find(
    (m) => m.label.toLowerCase() === selectedMood.toLowerCase()
  );

  const getTemplateType = () => {
    let templateType: "plain" | "template" | "image" | "mixed" = "plain";
    const hasImages = selectedImages.length > 0 || existingImageUrls.length > 0;

    if (hasImages) {
      templateType = postContent.trim() ? "mixed" : "image";
    } else if (activeSection || therapyData) {
      templateType = "template";
    }
    return templateType;
  };

  const handlePostBackend = async () => {
    if (
      !postContent.trim() &&
      selectedImages.length === 0 &&
      existingImageUrls.length === 0 &&
      !therapyData
    ) {
      return;
    }

    setLoading(true);

    try {
      // Step 1: Compress and upload new images
      let newImageData: any[] = [];
      if (selectedImages.length > 0) {
        const compressedImages = await compressImages(
          selectedImages,
          CompressionPresets.MOBILE_OPTIMIZED
        );
        const uploadResults = await uploadImagesBackendFetch(compressedImages);
        newImageData = uploadResults;
      }

      // Combine existing images with newly uploaded ones
      const allImageData = [
        ...existingImageUrls.map((url) => ({ url })),
        ...newImageData,
      ];

      // Prepare therapy data
      let therapyDataForBackend = undefined;
      let therapyFrequency = undefined;

      if (therapyData) {
        therapyFrequency = therapyData.period || undefined;
        therapyDataForBackend = {
          period: therapyData.period,
          date: therapyData.date,
          dateRange: therapyData.dateRange,
          therapies: therapyData.therapies,
        };
      }

      const templateType = getTemplateType();
      const postData = {
        description: postContent.trim() || undefined,
        template_type: templateType,
        visibility:
          selectedAudience.toLowerCase() === "private"
            ? ("private" as const)
            : ("followers" as const),
        feelings: selectedMood || undefined,
        images: allImageData.length > 0 ? allImageData : undefined,
        therapy_data: templateType === "plain" ? null : therapyDataForBackend,
        therapy_frequency: therapyFrequency,
      };

      if (isEditMode && editPost) {
        // Update existing post
        await updatePost(editPost.id, postData);
        toastBar.success("Reflection updated!", 2000, "bottom");
        if (onEditComplete) {
          onEditComplete();
        }
      } else {
        // Create new post
        const result = await createPost(postData);
        onPost && onPost(result as any);
        toastBar.success("Reflection added!", 2000, "bottom");
      }

      resetModal();
      // onDidDismiss(true);
    } catch (error: any) {
      console.error("Backend upload flow error:", error);
      toastBar.error(error.message || "Failed to save post", 3000, "bottom");
    } finally {
      setLoading(false);
    }
  };

  const onImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      handleImageSelect(fileArray);
    }
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = (refresh = true) => {
    onDidDismiss(refresh);
    resetModal();
  };

  const resetModal = () => {
    setPostContent("");
    setSelectedMood("");
    setActiveSection(null);
    setTherapyData(null);
    setSelectedImages([]);
    setImagePreviews([]);
    setExistingImageUrls([]);
    setSelectedAudience("Followers");
    setShowAudiencePopover(false);
    setShowMoodSelector(false);
    setPreSelected && setPreSelected(null);
  };

  const handleTherapyDataSelect = (data: any) => {
    console.log("Selected therapy data:", data);
    setTherapyData(data);
  };

  const handleRemoveTherapyData = () => {
    setTherapyData(null);
    setActiveSection(null);
  };

  const handleImageSelect = (files: File[]) => {
    const totalImages =
      selectedImages.length + existingImageUrls.length + files.length;
    if (totalImages > 5) {
      toaster.info("Maximum 5 images allowed.", 3000, "bottom");
      // return;
    }

    const allowedFiles = files.slice(
      0,
      Math.max(0, 5 - selectedImages.length - existingImageUrls.length)
    );
    setSelectedImages((prev) => [...prev, ...allowedFiles]);

    allowedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index: number) => {
    setExistingImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSectionChange = (section: SectionType | null) => {
    setActiveSection(section);
    if (section) {
      const sectionData = templateCards.find((card) => card.key === section);
      // setPostContent(sectionData?.template || "");
      setPostPlaceHolder(sectionData?.description || "What's on your mind?");
      setInputFieldCharIndex(sectionData?.inputCharIndex || null);
    } else {
      setPostContent("");
      setPostPlaceHolder("What's on your mind?");
      setInputFieldCharIndex(null);
      setTherapyData(null);
    }
  };

  // Focus textarea and set cursor position when template is selected
  useEffect(() => {
    console.log("inputFieldCharIndex:", inputFieldCharIndex);
    if (inputFieldCharIndex !== null && textareaRef.current) {
      // Wait for the textarea to update with new content
      setTimeout(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          // Get the native textarea element
          textarea.getInputElement().then((nativeTextarea) => {
            if (nativeTextarea) {
              nativeTextarea.focus();
              nativeTextarea.setSelectionRange(
                inputFieldCharIndex,
                inputFieldCharIndex
              );
            }
          });
        }
      }, 100);
    }
  }, [inputFieldCharIndex]);

  const handleAudienceChange = (audience: string) => {
    setSelectedAudience(audience);
    setShowAudiencePopover(false);
  };

  const handleMoodChange = (mood: string) => {
    setSelectedMood(mood);
    setShowMoodSelector(false);
  };

  const handleToggleAudiencePopover = () => {
    setShowAudiencePopover(!showAudiencePopover);
    setShowMoodSelector(false);
  };

  const handleToggleMoodSelector = () => {
    setShowMoodSelector(!showMoodSelector);
    setShowAudiencePopover(false);
  };

  const hasContent =
    postContent.trim() ||
    selectedImages.length > 0 ||
    existingImageUrls.length > 0 ||
    therapyData;
  const isLargerScreen = window.innerWidth >= 768;

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => handleClose(false)}
      breakpoints={isLargerScreen ? undefined : [0, 1]}
      initialBreakpoint={isLargerScreen ? undefined : 1}
      handle={false}
      className="add-post-modal"
      style={{
        "--border-radius": "16px 16px 0 0",
      }}
    >
      <IonToolbar
        className="flex items-center justify-center"
        style={{
          "--background": "#F7F5FA",
        }}
      >
        <IonButtons slot="start">
          <IonButton
            className=""
            onClick={() => handleClose(false)}
            disabled={loading}
            fill="clear"
          >
            <IonIcon icon={close} className="close-icon" />
          </IonButton>
        </IonButtons>
        <IonTitle className="modal-title">
          {isEditMode ? "Edit post" : "Add post"}
        </IonTitle>
        <IonButtons slot="end">
          <IonButton
            onClick={handlePostBackend}
            disabled={!hasContent || loading}
            className={`post-button ${
              hasContent ? "post-button-enabled" : "post-button-disabled"
            }`}
          >
            {isEditMode ? "Update" : "Post"}
          </IonButton>
        </IonButtons>
      </IonToolbar>
      <IonContent
        className="modal-content"
        style={{
          "--background": "#F7F5FA",
        }}
      >
        <div className="content-wrapper">
          <div className="main-content">
            <div className="user-input-section">
              <Avatar
                initials={getInitials(
                  user?.first_name || "",
                  user?.last_name || ""
                )}
                imageSrc={user?.avatar}
                imageClassName="rounded-[12px] border"
                innerClassName="avatar-inner-38"
              />

              <div className="input-container">
                {selectedMood && (
                  <div className="mood-display">
                    <span className="feeling-label">I am feeling</span>
                    {selectedMoodObj?.icon &&
                      (() => {
                        const MoodIcon = selectedMoodObj.icon;
                        return (
                          <MoodIcon
                            className="w-5 h-5"
                            aria-label={selectedMoodObj.label}
                          />
                        );
                      })()}
                    <span className="mood-label">{selectedMoodObj?.label}</span>
                  </div>
                )}

                <div
                  className="text-editor-container"
                  style={{ borderBottom: "1px solid #e0e0e0" }}
                >
                  <IonTextarea
                    ref={textareaRef}
                    className="post-textarea"
                    placeholder={postPlaceHolder}
                    autoGrow
                    value={postContent}
                    onIonInput={(e) => setPostContent(e.detail.value || "")}
                    disabled={loading}
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* Existing images from the post being edited */}
            {existingImageUrls.length > 0 && (
              <div className="image-previews">
                {existingImageUrls.map((url, index) => (
                  <div key={`existing-${index}`} className="image-preview">
                    <AuthenticatedImage
                      key={index}
                      src={url}
                      alt={`Post image ${index + 1}`}
                      className="post-image"
                      images={existingImageUrls}
                      index={index}
                    />
                    <button
                      onClick={() => removeExistingImage(index)}
                      className="remove-image-button"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* New images being added */}
            {imagePreviews.length > 0 && (
              <div className="image-previews">
                {imagePreviews.map((preview, index) => (
                  <div key={`new-${index}`} className="image-preview">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="preview-image"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="remove-image-button"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {therapyData && therapyData.therapies && (
              <div className="therapy-card-preview-container">
                <IonButton
                  className="remove-therapy-button"
                  onClick={handleRemoveTherapyData}
                  disabled={loading}
                  fill="clear"
                >
                  <IonIcon icon={close} className="close-icon" />
                </IonButton>
                <BiWazeTherapyCard
                  title="Weekly Summary"
                  currentMonth={new Date().toLocaleString("default", {
                    month: "long",
                  })}
                  therapies={therapyData.therapies}
                  hideFooter={true}
                  timePeriod={
                    therapyData.period === "weekly" ? "Week" : undefined
                  }
                />
              </div>
            )}
          </div>
        </div>
      </IonContent>

      <IonFooter>
        <IonToolbar className="comment-input-toolbar " style={{}}>
          <SectionCards
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            onTherapyDataSelect={handleTherapyDataSelect}
            journalCategories={journalCategories}
            templateCards={templateCards}
            toaster={toaster}
          />
          <BottomActionsBar
            selectedAudience={selectedAudience}
            selectedMood={selectedMood}
            onAudienceChange={handleAudienceChange}
            onMoodChange={handleMoodChange}
            onImageSelect={handleImageSelect}
            showAudiencePopover={showAudiencePopover}
            showMoodSelector={showMoodSelector}
            onToggleAudiencePopover={handleToggleAudiencePopover}
            onToggleMoodSelector={handleToggleMoodSelector}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            multiple
            onChange={onImageSelect}
            className="hidden"
          />
        </IonToolbar>
      </IonFooter>

      {loading && (
        <Loader
          loadingMessage={isEditMode ? "Updating.." : "Posting.."}
          className="bg-[#fafafa] bg-opacity-50"
        />
      )}
    </IonModal>
  );
};

export default AddPostModal;
